import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import Radio from '@mui/material/Radio'
import colors from '@settings/styleSettings'
import { CSVLink } from 'react-csv'
import Loading from '@components/loading'
import PrimaryButton from '@components/inputs/button/primaryButton'
import Modal from '..'

import {
  Container,
  Title,
  Subtitle,
  Footer,
  CloseIcon,
  RadioArea,
  FieldsArea,
} from './styles'

import {
  getIfIsDiabetic,
  getIfIsDyslipidemic,
  getIfIsHypertensive,
  getImc,
  getRcq,
} from '@util/equationsUtil'

import {
  getRiskClassification,
  getRisks,
} from '@util/classificationUtil'

import { csvOptionsEnum } from '@util/csvOptionsEnum'
import {
  getCsvData as getCsvDataAction,
  getCountBeneficiariesByCompanyOrProgramme as getCountAction
} from '@actions/beneficiary'
import { useParams } from 'react-router-dom'

import 'material-icons/iconfont/outlined.css'

const SpreadSheetModal = ({
  isOpen,
  setClose,
  searchTerm,
  filterState,
  getCsvData,
  selectedBeneficiaries,
  filteredTotal,
  total,
  getCountBeneficiariesByCompanyOrProgramme,
  companyId: companyIdProp,
  programmeId: programmeIdProp,
}) => {
  const [selectedValue, setSelectedValue] = useState('')
  const [CSVData, setCSVData] = useState([])
  const [loading, setLoading] = useState(false)
  const [beneficiaries, setBeneficiaries] = useState([])
  const [totalBeneficiaries, setTotalBeneficiaries] = useState(0)
  const csvRef = useRef()
  const { programmeId: programmeIdParam, companyId: companyIdParam } = useParams()
  const companyId = companyIdProp || companyIdParam
  const programmeId = programmeIdProp || programmeIdParam
  function riskToString(someRisk) {
    return someRisk > 0 ? 'Sim' : 'Não'
  }

  function numberFormater(number) {
    return Number.isNaN(number) ? null : number.toFixed(2).replace('.', ',')
  }

  useEffect(() => {
    countTotalBeneficiaries()
  }, [isOpen])

  useEffect(() => {
    if (loading && Array.isArray(beneficiaries) && beneficiaries.length > 0) {
      const risks = calculateRisks(beneficiaries)
      const beneficiariesWithRisks = addRiskDataToBeneficiaries(
        beneficiaries,
        risks
      )
      setCSVData(beneficiariesWithRisks)
    }
    setLoading(false)
  }, [beneficiaries])

  useEffect(() => {
    if (CSVData && csvRef.current && csvRef.current.link) {
      csvRef.current.link.dataset.interception = 'off'
      csvRef.current.link.click()
      setCSVData([])
    }
  }, [CSVData])

  const renderLoading = () => (
    <Loading color={colors.strongWhite} height={30} width={30} />
  )

  function searchBeneficiaries(size, selected) {
    selected === 'allBeneficiaries'
      ? getCsvData(
        {
          page: 0,
          searchTerm,
          pageSize: size,
          companyId,
          programmeId,
        },
        (data) => setBeneficiaries(data)
      )
      : selected === 'filteredBeneficiaries'
  && (() => {
    const {
      startDate,
      endDate,
      ...restFilters
    } = filterState

    const payload = {
      page: 0,
      ...restFilters,
      searchTerm,
      pageSize: size,
      companyId,
      programmeId,
    }

    if (startDate) payload.startDate = startDate
    if (endDate) payload.endDate = endDate

    getCsvData(payload, (data) => setBeneficiaries(data))
  })()
  }

  const countTotalBeneficiaries = () => {
    if (companyId) {
      getCountBeneficiariesByCompanyOrProgramme({ companyId }, (data) => {
        setTotalBeneficiaries(data)
      })
    } else if (programmeId) {
      getCountBeneficiariesByCompanyOrProgramme({ programmeId }, (data) => {
        setTotalBeneficiaries(data)
      })
    } else {
      setTotalBeneficiaries(total)
    }
  }

  useEffect(() => {
    countTotalBeneficiaries()
  }, [companyId, programmeId])

  function calculateRisks(beneficiariesToCalculateRisks) {
    return beneficiariesToCalculateRisks.map((beneficiary) => {
      const lastCollection = beneficiary.lastGeneralCollection || {}
      const {
        systBloodPressure,
        diastBloodPressure,
        glucose,
        totalCholesterol,
        hdlCholesterol,
        triglycerides,
        ldlCholesterol,
        weight,
        height,
        waistCircumference,
        hipCircumference,
        hasDiabetes,
        hasSystemicArterialHypertension,
      } = lastCollection

      const imcValue = getImc(weight, height)
      const overweightForCsv = imcValue > 25 ? 'Sim' : 'Não'

      const rcq = waistCircumference > 0 && hipCircumference > 0
        ? numberFormater(getRcq(waistCircumference, hipCircumference))
        : null

      const hypertensiveRisk = getIfIsHypertensive(
        systBloodPressure,
        diastBloodPressure,
        hasSystemicArterialHypertension
      )

      const diabeticRisk = getIfIsDiabetic(glucose, hasDiabetes)

      const dyslipidemicRisk = getIfIsDyslipidemic(
        totalCholesterol,
        hdlCholesterol,
        triglycerides,
        ldlCholesterol
      )

      const risksOutput = getRisks(
        hypertensiveRisk,
        diabeticRisk,
        dyslipidemicRisk,
        imcValue
      )

      // eslint-disable-next-line no-nested-ternary
      const currentRisksLength = Array.isArray(risksOutput)
        ? risksOutput.length
        : typeof risksOutput === 'number'
          ? risksOutput
          : 0

      const riskClassification = getRiskClassification(currentRisksLength)

      const diabeticRiskForCsv = riskToString(diabeticRisk)
      const hypertensiveRiskForCsv = riskToString(hypertensiveRisk)
      const dyslipidemicRiskForCsv = riskToString(dyslipidemicRisk)
      const imcValueForCsv = numberFormater(imcValue)

      return {
        riskClassification,
        diabeticRisk: diabeticRiskForCsv,
        dyslipidemicRisk: dyslipidemicRiskForCsv,
        hypertensiveRisk: hypertensiveRiskForCsv,
        overweight: overweightForCsv,
        imcValue: imcValueForCsv,
        rcq,
      }
    })
  }

  function addRiskDataToBeneficiaries(beneficiariesRisks, risks) {
    const combined = beneficiariesRisks.map((beneficiary, index) => {
      return {
        ...beneficiary,
        risk: risks[index],
      }
    })
    return combined
  }

  const toBr = (date) => {
    const pad = (n) => n.toString().padStart(2, '0')
    return (
      `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} `
      + `${pad(date.getHours())}:${pad(date.getMinutes())}`
    )
  }

  const formatDateTime = (str) => {
    if (!str) return 'Não coletado'

    let d = new Date(str)
    if (isValid(d)) return toBr(d)

    const m = str.match(
      /^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2})(?::(\d{2}))?$/
    )
    if (m) {
      const [, dd, MM, yyyy, hh, mm, ss = '0'] = m
      d = new Date(+yyyy, +MM - 1, +dd, +hh, +mm, +ss)
      if (isValid(d)) return toBr(d)
    }
    return ' '
  }

  function isValid(date) {
    return date instanceof Date && !Number.isNaN(date.getTime())
  }

  const headers = [
    'Nome',
    'CPF',
    'Carteirinha',
    'Data de Nascimento',
    'Empresa',
    'Pressão Sistólica',
    'Pressão Diastólica',
    'Risco',
    'IMC',
    'Diabetes',
    'Dislipidemia',
    'Hipertensão',
    'Sobrepeso',
    'Altura',
    'Circ, Abdominal',
    'Circ, da Cintura',
    'Circ, do Quadril',
    'RCQ',
    'Peso',
    'IMC',
    'Gordura(%)',
    'Massa Magra',
    'Calorias',
    'Gordura Visceral',
    'Glicose',
    'Colesterol LDL',
    'Colesterol HDL',
    'Colesterol VLDL',
    'Triglicerideos',
    'Colesterol Total',
    'Não HDL-C',
    'Data da Coleta',
  ]

  const csvContent = [
    headers,
    ...CSVData.map(
      ({
        name, cpf, insuranceNumber, birthdate, company, lastGeneralCollection: lastCollection, risk
      }) => [
        name,
        `'${cpf}`,
        insuranceNumber,
        birthdate,
        company?.name,
        lastCollection?.systBloodPressure,
        lastCollection?.diastBloodPressure,
        risk?.riskClassification,
        risk?.imcRisk,
        risk?.diabeticRisk,
        risk?.dyslipidemicRisk,
        risk?.hypertensiveRisk,
        risk?.overweight,
        lastCollection?.height,
        lastCollection?.abdominalCircumference,
        lastCollection?.waistCircumference,
        lastCollection?.hipCircumference,
        risk?.rcq,
        lastCollection?.weight,
        risk?.imcValue,
        lastCollection?.fat,
        lastCollection?.leanMass,
        lastCollection?.calories,
        lastCollection?.visceralFat,
        lastCollection?.glucose,
        lastCollection?.ldlCholesterol,
        lastCollection?.hdlCholesterol,
        lastCollection?.vldlCholesterol,
        lastCollection?.triglycerides,
        lastCollection?.totalCholesterol != null ? lastCollection.totalCholesterol.toFixed(1) : '',
        lastCollection?.notHdl?.toFixed(1),
        formatDateTime(lastCollection?.collectedAt),
      ]
    ),
  ]
  const setBeneficiariesToExport = () => {
    if (selectedValue === 'allBeneficiaries') {
      setLoading(true)
      searchBeneficiaries(total, selectedValue)
    } else if (selectedValue === 'filteredBeneficiaries') {
      setLoading(true)
      searchBeneficiaries(filteredTotal, selectedValue)
    } else if (selectedValue === 'selectedBeneficiaries') {
      setLoading(true)
      const risks = calculateRisks(selectedBeneficiaries)
      const beneficiariesWithRisks = addRiskDataToBeneficiaries(
        selectedBeneficiaries,
        risks
      )
      setCSVData(beneficiariesWithRisks)
      setLoading(false)
    }
  }

  const handleOptionChange = (event) => {
    setSelectedValue(event.target.value)
  }

  const handleExportCsvOptions = (value) => {
    if (value === 'allBeneficiaries') return totalBeneficiaries
    if (value === 'filteredBeneficiaries') return filteredTotal

    return selectedBeneficiaries.length
  }

  return (
    <Modal isOpen={isOpen} onRequestClose={setClose}>
      <CloseIcon className="material-icons-outlined" onClick={setClose}>close</CloseIcon>
      <Container>
        <Title>Exportar planilha</Title>
        <Subtitle>Selecione o que deseja exportar:</Subtitle>
        <FieldsArea>
          {csvOptionsEnum.map(({ value, label }) => (
            <RadioArea selected={selectedValue === value} key={value}>
              <Radio
                checked={selectedValue === value}
                onChange={handleOptionChange}
                value={value}
                name="radio"
                inputProps={{ 'aria-label': true }}
                style={{ color: colors.primary }}
              />
              {`${label} (${handleExportCsvOptions(value)})`}
            </RadioArea>
          ))}
        </FieldsArea>

        <Footer>Será exportada uma planilha em formato .csv</Footer>

        <PrimaryButton
          style={{ marginTop: '18px' }}
          onClick={() => setBeneficiariesToExport()}
        >
          {loading ? renderLoading() : 'Confirmar'}
        </PrimaryButton>

        {CSVData.length > 0 && (
          <CSVLink
            data={csvContent}
            style={{ color: 'white', textDecoration: 'none' }}
            onClick={setClose}
            filename="Relatorio_de_saude_preventiva"
            ref={csvRef}
            target="_blank"
          />
        )}
      </Container>
    </Modal>
  )
}

SpreadSheetModal.propTypes = {
  isOpen: PropTypes.bool,
  setClose: PropTypes.func.isRequired,
  searchTerm: PropTypes.string,
  getCsvData: PropTypes.func.isRequired,
  filterState: PropTypes.shape({
    noRisk: PropTypes.bool,
    highRisk: PropTypes.bool,
    mediumRisk: PropTypes.bool,
    lowRisk: PropTypes.bool,
    diabetic: PropTypes.bool,
    hypertensive: PropTypes.bool,
    overweight: PropTypes.bool,
    dyslipidemic: PropTypes.bool,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }),
  selectedBeneficiaries: PropTypes.array,
  total: PropTypes.number,
  filteredTotal: PropTypes.number,
  getCountBeneficiariesByCompanyOrProgramme: PropTypes.func.isRequired,
  companyId: PropTypes.number,
  programmeId: PropTypes.number,
}

SpreadSheetModal.defaultProps = {
  isOpen: false,
  searchTerm: '',
  filterState: {
    noRisk: false,
    highRisk: false,
    mediumRisk: false,
    lowRisk: false,
    diabetic: false,
    hypertensive: false,
    overweight: false,
    dyslipidemic: false,
  },
  selectedBeneficiaries: [],
  total: null,
  filteredTotal: null,
  companyId: undefined,
  programmeId: undefined,
}

const mapStateToProps = ({ beneficiary }) => ({
  filteredTotal: beneficiary.total,
  total: beneficiary?.totalBeneficiaries,
})

const mapDispatchToProps = (dispatch) => bindActionCreators(
  {
    getCsvData: getCsvDataAction,
    getCountBeneficiariesByCompanyOrProgramme: getCountAction
  },
  dispatch
)

export default connect(mapStateToProps, mapDispatchToProps)(SpreadSheetModal)
